import * as THREE from "three";
import { ROOF_HEIGHT, TRUCK_WIDTH, trailerDepth } from "../../data/entities";
import { FACE, G, Kit, atlasOf, col, ethMark, hdr, instance, once, profileSlab, tiledPlane, type Atlas } from "./kit";
import { blobShadow, decalMaterial, glowSprite, printMaterial } from "./fx";
import { addWheel } from "./parts";
import { beamTex, canvasTex, corrugationTex, labelTex, stripesTex, DISPLAY_FONT } from "./tex";

/**
 * ETH road trains: corrugated containers on skeletal trailers, a cab-over tractor with a full-height fairing (its
 * roof is the trucks' roof, 5.44 m, so the whole train can be driven on), wheels, lights and ETH crystals.
 */

const ROOF = ROOF_HEIGHT;                 // 5.44
const HALF = TRUCK_WIDTH / 2;             // 1.7
const BOX_BOTTOM = 1.3;
const CAB_LENGTH = 6.6;
const WR = 0.52, WW = 0.4;                // wheel radius / width
export const CONTAINER_COLORS = [0xe8423a, 0x2f6fe4, 0x22a85a, 0xff8a1f, 0x8b46ff];
const CAB_COLORS = [0xf2f4f8, 0x2a2e3a, 0xe8423a, 0x2f6fe4, 0xffb52e];

const corrugated = (): THREE.MeshStandardMaterial => once("mat:corrugated", () => new THREE.MeshStandardMaterial({
  vertexColors: true, normalMap: corrugationTex(), normalScale: new THREE.Vector2(1.4, 1.4), roughness: 0.48, metalness: 0.28
}));

/** Rear doors, grey so the vertex colour tints them: seams, hinges, recessed panels. */
const doorTex = (): THREE.Texture => canvasTex("container-doors", 256, 256, (ctx, w, h) => {
  ctx.fillStyle = "#ececec";
  ctx.fillRect(0, 0, w, h);
  for (const x0 of [0, w / 2]) {
    for (let i = 0; i < 4; i++) {
      const y = 14 + i * 60;
      const g = ctx.createLinearGradient(0, y, 0, y + 48);
      g.addColorStop(0, "#c4c4c4"); g.addColorStop(0.15, "#f6f6f6"); g.addColorStop(0.85, "#dadada"); g.addColorStop(1, "#a8a8a8");
      ctx.fillStyle = g;
      ctx.fillRect(x0 + 12, y, w / 2 - 24, 48);
    }
  }
  ctx.fillStyle = "#4a4a4a";
  ctx.fillRect(w / 2 - 3, 0, 6, h);
  ctx.strokeStyle = "#7d7d7d";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, w - 6, h - 6);
  ctx.fillStyle = "#3c3c3c";
  for (const y of [22, 96, 160, 226]) { ctx.fillRect(2, y, 18, 12); ctx.fillRect(w - 20, y, 18, 12); }
});

const plateTex = (): THREE.Texture => labelTex("plate-eth", 256, 96, (ctx, w, h) => {
  ctx.fillStyle = "#f4f4f0";
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "#1d4fd8";
  ctx.fillRect(0, 0, 30, h);
  ctx.strokeStyle = "#111";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, w - 6, h - 6);
  ctx.fillStyle = "#111";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 58px ${DISPLAY_FONT}`;
  ctx.fillText("ETH 2.0", w / 2 + 14, h / 2 + 3);
});

/** Every texture a truck uses in one atlas, with the corrugation as its normal map: a truck is one lit mesh. */
const truckAtlas = (): Atlas => atlasOf("truck", [doorTex(), guardTex(), plateTex()], { map: corrugationTex(), scale: 1.4 });
const guardTex = (): THREE.Texture => stripesTex("#e8423a", "#ffffff", 7, 512, 48);

/** A roof panel whose u runs along z (ribs across the width). */
function roofPlane(length: number, width: number): THREE.BufferGeometry {
  return once(`g:roofplane:${length}:${width}`, () => {
    const g = tiledPlane(length, width, length / 0.6, 1).clone();
    g.rotateX(-Math.PI / 2);
    g.rotateY(Math.PI / 2);
    return g;
  });
}

export interface TruckOptions {
  /** 1–5 trailers (the entity's `trailers`); 1 = a lone trailer on landing legs */
  trailers: number;
  /** colour variety (the scene passes entity.id % 3) */
  variant?: number;
  /** a "mover": cab first, headlights and beams on (the whole truck is turned to face −z) */
  oncoming?: boolean;
}

/**
 * `buildTruck({ trailers, variant, oncoming })` → origin = centre of the train's footprint (length
 * trailerDepth(trailers)), y = 0 on the road. Cab at +z for parked trucks; `oncoming` turns the inner group by π so
 * the cab and its lights face −z (the player). Roof flat at ROOF_HEIGHT over the whole length.
 */
export function buildTruck(opts: TruckOptions): THREE.Group {
  const n = Math.max(1, Math.min(5, Math.round(opts.trailers)));
  const variant = ((opts.variant ?? 0) % 5 + 5) % 5;
  const oncoming = opts.oncoming === true;
  return instance(`truck:${n}:${variant}:${oncoming}`, () => {
    const total = trailerDepth(n);
    const cab = n === 1 ? 0 : CAB_LENGTH;
    const each = (total - cab) / n;
    const kit = new Kit(truckAtlas());                    // lit parts + doors + guard + plate + corrugation: one mesh
    for (let i = 0; i < n; i++) {
      const zc = -total / 2 + each * (i + 0.5);
      wagon(kit, zc, each - 0.5, CONTAINER_COLORS[(variant + i) % 5], i === 0, i < n - 1, n === 1);
    }
    if (cab > 0) tractor(kit, total / 2, CAB_COLORS[variant], CONTAINER_COLORS[(variant + n - 1) % 5], oncoming);
    const body = kit.build({ receive: true, name: "truck" });
    const inner = new THREE.Group();
    inner.add(body, blobShadow(4.6, total + 1.2));
    if (oncoming && cab > 0) {
      const beamMat = once("mat:beam", () => new THREE.MeshBasicMaterial({ map: beamTex(), transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
      const beam = new THREE.Mesh(G.plane(3.6, 14), beamMat);
      beam.rotation.x = -Math.PI / 2;
      beam.position.set(0, 0.04, total / 2 + 7.0);
      beam.name = "beam";
      inner.add(beam);
      for (const x of [-1.12, 1.12]) {
        const g = glowSprite(0xfff3c8, 2.2, 0.75);
        g.position.set(x, 1.55, total / 2 + 0.25);
        g.name = "headGlow";
        inner.add(g);
      }
      inner.rotation.y = Math.PI;
    }
    const root = new THREE.Group();
    root.name = oncoming ? "truckOncoming" : "truck";
    root.add(inner);
    return root;
  });
}

/** One container on its trailer: `rear` = the train's last wagon (doors, lights, guard), `coupled` = a drawbar ahead. */
function wagon(kit: Kit, zc: number, L: number, color: number, rear: boolean, coupled: boolean, lone: boolean): void {
  const frame = col(color).multiplyScalar(0.62);
  const zR = zc - L / 2, zF = zc + L / 2;
  const panelH = ROOF - 0.16 - (BOX_BOTTOM + 0.26);
  const panelY = BOX_BOTTOM + 0.26 + panelH / 2;
  const corr = corrugated();
  // corrugated sides, front wall and roof
  const side = tiledPlane(L - 0.36, panelH, (L - 0.36) / 0.46, 1);
  kit.add(side, corr, color, { p: [HALF - 0.05, panelY, zc], r: FACE.left });
  kit.add(side, corr, color, { p: [-HALF + 0.05, panelY, zc], r: FACE.right });
  kit.add(tiledPlane(TRUCK_WIDTH - 0.36, panelH, (TRUCK_WIDTH - 0.36) / 0.46, 1), corr, color, { p: [0, panelY, zF - 0.05], r: FACE.back });
  kit.add(roofPlane(L - 0.2, TRUCK_WIDTH - 0.3), corr, col(color).lerp(new THREE.Color(0xffffff), 0.12), { p: [0, ROOF - 0.03, zc] });
  // frame: corner posts, top and bottom rails
  for (const x of [-1, 1]) {
    for (const z of [zR + 0.09, zF - 0.09]) kit.add(G.box(0.18, ROOF - BOX_BOTTOM, 0.18), "paint", frame, { p: [x * (HALF - 0.09), (ROOF + BOX_BOTTOM) / 2, z] });
    kit.add(G.box(0.16, 0.16, L), "paint", frame, { p: [x * (HALF - 0.08), ROOF - 0.08, zc] });
    kit.add(G.box(0.18, 0.26, L), "paint", frame, { p: [x * (HALF - 0.09), BOX_BOTTOM + 0.13, zc] });
  }
  for (const z of [zR + 0.08, zF - 0.08]) {
    kit.add(G.box(TRUCK_WIDTH, 0.16, 0.16), "paint", frame, { p: [0, ROOF - 0.08, z] });
    kit.add(G.box(TRUCK_WIDTH, 0.26, 0.18), "paint", frame, { p: [0, BOX_BOTTOM + 0.13, z] });
  }
  // the ETH crystal on both sides
  const crystal = ethMark(2.5, 0.12);
  kit.add(crystal, "paint", 0xffffff, { p: [HALF - 0.04, panelY, zc], r: FACE.left });
  kit.add(crystal, "paint", 0xffffff, { p: [-HALF + 0.04, panelY, zc], r: FACE.right });
  // rear doors
  kit.add(G.plane(TRUCK_WIDTH - 0.36, panelH), printMaterial("container-doors", doorTex(), { tinted: true, roughness: 0.5, metalness: 0.25 }), color, { p: [0, panelY, zR + 0.05], r: FACE.front });

  // the trailer: beams, skirts, axles, wheels, mudguards
  const dark = 0x2b2d36;
  for (const x of [-0.6, 0.6]) kit.add(G.box(0.2, 0.32, L - 0.3), "satin", dark, { p: [x, BOX_BOTTOM - 0.16, zc] });
  const axles = [zR + 1.25, zF - 1.25];
  const skirt = L - 2 * 1.95;
  if (skirt > 0.5) for (const x of [-1, 1]) kit.add(G.box(0.06, 0.62, skirt), "plastic", 0x3a3d48, { p: [x * 1.56, 0.74, zc] });
  for (const z of axles) {
    if (lone && z === axles[1]) continue;                      // a lone trailer stands on legs at the front
    kit.add(G.cyl(0.08, 0.08, 2.3, 8, true), "satin", 0x24252c, { p: [0, WR, z], r: [0, 0, Math.PI / 2] });
    for (const x of [-1, 1]) {
      addWheel(kit, [x * 1.3, WR, z], WR, WW, x as 1 | -1);
      kit.add(G.box(0.5, 0.07, 1.22), "plastic", 0x1d1e24, { p: [x * 1.3, WR * 2 + 0.12, z] });
    }
  }
  if (lone) {
    for (const x of [-0.9, 0.9]) {
      kit.add(G.box(0.14, BOX_BOTTOM - 0.1, 0.14), "satin", 0x5b5f6c, { p: [x, (BOX_BOTTOM - 0.1) / 2 + 0.05, zF - 1.4] });
      kit.add(G.box(0.36, 0.08, 0.36), "satin", 0x5b5f6c, { p: [x, 0.04, zF - 1.4] });
    }
  }
  // amber side markers
  for (const x of [-1, 1]) for (const z of [zR + 0.6, zF - 0.6]) kit.add(G.box(0.04, 0.1, 0.22), "glow", hdr(0xffa21f, 1.4), { p: [x * (HALF + 0.005), BOX_BOTTOM + 0.13, z] });
  if (coupled) kit.add(G.box(0.24, 0.2, 1.1), "satin", 0x24252c, { p: [0, BOX_BOTTOM - 0.3, zF + 0.25] });

  if (rear) {
    // locking bars and handles
    for (const x of [-1.08, -0.34, 0.34, 1.08]) {
      kit.add(G.cyl(0.035, 0.035, panelH - 0.3, 6, true), "chrome", 0xdfe3ea, { p: [x, panelY, zR - 0.02] });
      kit.add(G.box(0.08, 0.36, 0.08), "chrome", 0xdfe3ea, { p: [x + (x < 0 ? -0.09 : 0.09), panelY - 0.4, zR - 0.05] });
      for (const y of [BOX_BOTTOM + 0.35, ROOF - 0.3]) kit.add(G.box(0.12, 0.1, 0.08), "satin", 0x6b6f7c, { p: [x, y, zR - 0.02] });
    }
    // light bar with tail, indicator and reverse lamps, the guard and the plate
    kit.add(G.box(TRUCK_WIDTH - 0.1, 0.3, 0.14), "plastic", 0x1d1e24, { p: [0, BOX_BOTTOM - 0.17, zR - 0.04] });
    for (const s of [-1, 1]) {
      kit.add(G.box(0.46, 0.18, 0.06), "glow", hdr(0xff1e30, 1.5), { p: [s * 1.3, BOX_BOTTOM - 0.17, zR - 0.12] });
      kit.add(G.box(0.22, 0.18, 0.06), "glow", hdr(0xffa21f, 1.4), { p: [s * 0.92, BOX_BOTTOM - 0.17, zR - 0.12] });
      kit.add(G.box(0.18, 0.18, 0.06), "glow", col(0xdfe6ef), { p: [s * 0.66, BOX_BOTTOM - 0.17, zR - 0.12] });
      kit.add(G.box(0.12, 0.5, 0.12), "satin", 0x2b2d36, { p: [s * 0.95, 0.82, zR + 0.12] });
    }
    for (const x of [-0.35, 0, 0.35]) kit.add(G.box(0.16, 0.08, 0.06), "glow", hdr(0xff1e30, 1.5), { p: [x, ROOF - 0.08, zR - 0.06] });
    kit.add(G.chamfer(2.9, 0.2, 0.14, 0.03), printMaterial("guard-stripes", guardTex(), { roughness: 0.4 }), 0xffffff, { p: [0, 0.6, zR + 0.02] });
    kit.add(G.plane(0.62, 0.23), decalMaterial("plate", plateTex(), { roughness: 0.35 }), 0xffffff, { p: [0, 0.98, zR - 0.13], r: FACE.front });
  }
}

/** The cab-over tractor at the front (+z): cab, full-height fairing, wheels, lights. */
function tractor(kit: Kit, zF: number, paint: number, accent: number, lightsOn: boolean): void {
  const zB = zF - CAB_LENGTH;
  const cabLen = 3.3;
  const W = 3.2;
  // the cab: a raked side profile, extruded and rounded
  const profile: [number, number][] = [
    [zF - cabLen, 1.15], [zF - 0.12, 1.15], [zF - 0.02, 1.4], [zF - 0.06, 3.05], [zF - 0.36, 4.62], [zF - 0.62, 5.3], [zF - 1.3, ROOF], [zF - cabLen, ROOF]
  ];
  kit.add(profileSlab(profile, W, 0.12, { round: [2, 5], curveSegments: 5 }), "gloss", paint);
  // fairing / sleeper behind it, the same height (the roof continues)
  const fairLen = CAB_LENGTH - cabLen - 0.08;
  kit.add(G.chamfer(W - 0.1, ROOF - 1.15, fairLen, 0.08), "gloss", paint, { p: [0, (ROOF + 1.15) / 2, zB + 0.04 + fairLen / 2] });
  kit.add(G.box(W - 0.3, 0.05, CAB_LENGTH - 0.3), "satin", col(paint).multiplyScalar(0.8), { p: [0, ROOF - 0.02, zB + CAB_LENGTH / 2 - 0.1] });
  // accent stripe along both sides
  for (const x of [-1, 1]) {
    kit.add(G.box(0.02, 0.28, CAB_LENGTH - 0.5), "gloss", accent, { p: [x * (W / 2 + 0.005), 2.55, zB + CAB_LENGTH / 2 - 0.05] });
    kit.add(G.box(0.02, 0.08, CAB_LENGTH - 0.5), "gloss", 0xffffff, { p: [x * (W / 2 + 0.006), 2.33, zB + CAB_LENGTH / 2 - 0.05] });
  }
  // the ETH crystal on the fairing sides
  const crystal = ethMark(2.1, 0.1);
  kit.add(crystal, "paint", accent, { p: [W / 2 - 0.06, 3.85, zB + 1.6], r: FACE.left });
  kit.add(crystal, "paint", accent, { p: [-W / 2 + 0.06, 3.85, zB + 1.6], r: FACE.right });
  // windscreen (raked), visor, side windows
  const rake = Math.atan2(0.3, 1.57);
  kit.add(G.chamfer(2.86, 1.6, 0.08, 0.03), "plastic", 0x15161b, { p: [0, 3.835, zF - 0.2], r: [-rake, 0, 0] });
  kit.add(G.chamfer(2.7, 1.42, 0.06, 0.02), "glass", 0x1c2a3d, { p: [0, 3.84, zF - 0.17], r: [-rake, 0, 0] });
  kit.add(G.chamfer(2.95, 0.08, 0.42, 0.03), "plastic", 0x1d1e24, { p: [0, 4.74, zF - 0.24], r: [-0.42, 0, 0] });
  for (const x of [-1, 1]) {
    kit.add(G.box(0.03, 1.15, 1.1), "glass", 0x1c2a3d, { p: [x * (W / 2 + 0.01), 3.85, zF - 1.05] });
    kit.add(G.box(0.025, 1.25, 1.2), "plastic", 0x15161b, { p: [x * (W / 2 + 0.003), 3.85, zF - 1.05] });
    // mirror arm + head
    kit.add(G.box(0.42, 0.05, 0.05), "satin", 0x2b2d36, { p: [x * (W / 2 + 0.2), 4.25, zF - 0.5] });
    kit.add(G.chamfer(0.14, 0.62, 0.3, 0.04), "plastic", 0x22242c, { p: [x * (W / 2 + 0.42), 3.98, zF - 0.5] });
    // steps under the door, fuel tank, exhaust stack
    for (const y of [0.7, 1.0]) kit.add(G.box(0.34, 0.06, 0.62), "satin", 0x8d93a2, { p: [x * 1.42, y, zF - 2.05] });
    kit.add(G.cyl(0.3, 0.3, 1.2, 10), "chrome", 0xd8dde6, { p: [x * 1.28, 0.74, zF - 3.0], r: [Math.PI / 2, 0, 0] });
    kit.add(G.cyl(0.11, 0.11, 3.7, 8, true), "chrome", 0xe2e6ee, { p: [x * (W / 2 + 0.12), 3.45, zF - cabLen - 0.15] });
    kit.add(G.cyl(0.13, 0.13, 0.12, 8), "chrome", 0xe2e6ee, { p: [x * (W / 2 + 0.12), 5.32, zF - cabLen - 0.15] });
  }
  // front: grille with chrome slats and the crystal, headlights, bumper, roof lights
  kit.add(G.chamfer(2.3, 1.2, 0.08, 0.03), "plastic", 0x17181e, { p: [0, 2.25, zF - 0.02] });
  for (let i = 0; i < 6; i++) kit.add(G.box(2.1, 0.06, 0.05), "chrome", 0xe6eaf0, { p: [0, 1.78 + i * 0.19, zF + 0.03] });
  kit.add(ethMark(0.62, 0.06), "chrome", 0xf4f6fa, { p: [0, 2.25, zF + 0.05] });
  for (const x of [-1, 1]) {
    kit.add(G.chamfer(0.66, 0.34, 0.1, 0.03), "chrome", 0xdfe3ea, { p: [x * 1.12, 1.56, zF - 0.01] });
    kit.add(G.chamfer(0.56, 0.24, 0.06, 0.02), lightsOn ? "glow" : "glass", lightsOn ? hdr(0xfff1c8, 1.8) : col(0xb8c4d4), { p: [x * 1.12, 1.56, zF + 0.04] });
    kit.add(G.box(0.2, 0.12, 0.05), "glow", hdr(0xffa21f, 1.3), { p: [x * 1.5, 1.56, zF + 0.0] });
  }
  kit.add(G.chamfer(W + 0.06, 0.44, 0.4, 0.06), "plastic", 0x24262e, { p: [0, 1.0, zF - 0.1] });
  for (let i = -2; i <= 2; i++) kit.add(G.box(0.16, 0.08, 0.1), "glow", hdr(0xffa21f, 1.4), { p: [i * 0.42, 5.34, zF - 0.66] });
  // chassis, wheels, mudguards
  for (const x of [-0.55, 0.55]) kit.add(G.box(0.22, 0.3, CAB_LENGTH - 0.4), "satin", 0x24252c, { p: [x, 0.92, zB + CAB_LENGTH / 2] });
  for (const z of [zF - 1.05, zB + 2.0, zB + 0.85]) {
    kit.add(G.cyl(0.08, 0.08, 2.3, 8, true), "satin", 0x24252c, { p: [0, WR, z], r: [0, 0, Math.PI / 2] });
    for (const x of [-1, 1]) {
      addWheel(kit, [x * 1.3, WR, z], WR, WW, x as 1 | -1);
      if (z !== zF - 1.05) kit.add(G.box(0.5, 0.07, 1.2), "plastic", 0x1d1e24, { p: [x * 1.3, WR * 2 + 0.1, z] });
    }
  }
}
